import { sanitizeParagraphHtml } from "./paragraphHtml";

/**
 * Inline icon support for sanitised paragraph/rich-text HTML.
 *
 * Wire format (what the editors store and the sanitisers preserve):
 * `<span data-icon="arrow-right"></span>` — an empty span whose name is
 * validated against {@link INLINE_ICON_NAMES}. At render time
 * {@link expandInlineIcons} replaces the marker with a static inline SVG
 * (1em, stroke `currentColor`, Feather visual language) so icons follow the
 * surrounding text's size and colour. Pure string transform: it works in
 * Next.js prerender, the admin preview iframe, and unit tests alike.
 */

const SVG_OPEN =
  '<svg class="bcc-inline-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';

/** Curated icon catalogue: name → SVG inner markup (24×24 stroke paths). */
const INLINE_ICON_PATHS: Record<string, string> = {
  "arrow-right": '<line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline>',
  "external-link":
    '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line>',
  check: '<polyline points="20 6 9 17 4 12"></polyline>',
  info: '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>',
  "alert-triangle":
    '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line>',
  download:
    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line>',
  "file-text":
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line>',
  mail: '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline>',
  phone:
    '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>',
  "map-pin":
    '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle>',
  calendar:
    '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line>',
  star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>',
};

/** French labels for the editor's icon picker. */
export const INLINE_ICON_LABELS: Record<string, string> = {
  "arrow-right": "Flèche",
  "external-link": "Lien externe",
  check: "Coche",
  info: "Information",
  "alert-triangle": "Avertissement",
  download: "Téléchargement",
  "file-text": "Document",
  mail: "Courriel",
  phone: "Téléphone",
  "map-pin": "Localisation",
  calendar: "Calendrier",
  star: "Étoile",
};

/** The valid icon names, in picker order. */
export const INLINE_ICON_NAMES: readonly string[] = Object.keys(INLINE_ICON_PATHS);

/** Full SVG markup of one icon (empty string for an unknown name). */
export function inlineIconSvg(name: string): string {
  const paths = INLINE_ICON_PATHS[name];
  return paths ? `${SVG_OPEN}${paths}</svg>` : "";
}

/**
 * Replaces every sanitised icon marker (`<span data-icon="name"></span>`)
 * with its inline SVG. Unknown names are removed. Already-expanded HTML is
 * untouched (idempotent — the SVG output no longer matches the marker).
 */
export function expandInlineIcons(html: string): string {
  if (typeof html !== "string" || !html) return "";
  return html.replace(
    /<span\s+data-icon="([a-z0-9-]{1,32})"\s*>\s*<\/span\s*>/gi,
    (_match, name: string) => inlineIconSvg(name.toLowerCase()),
  );
}

/**
 * Ouvre les liens ABSOLUS (http/https) dans un nouvel onglet, comme le fait
 * `RuntimeLink` pour les liens de composants : un lien éditorial vers un site
 * tiers ne doit pas faire quitter le site. `rel` neutralise `window.opener` et
 * la fuite de referer. Fait ICI, au rendu, et pas dans `sanitizeParagraphHtml`
 * qui doit rester octet pour octet identique à son miroir de l'admin.
 */
function openExternalLinksInNewTab(html: string): string {
  if (typeof html !== "string" || !html) return "";
  return html.replace(
    /<a href="(https?:\/\/[^"]*)">/gi,
    (_match, href: string) => `<a href="${href}" target="_blank" rel="noopener noreferrer">`,
  );
}

/**
 * The standard render path for paragraph-grade HTML: sanitise to the minimal
 * whitelist, expand icon markers, then send absolute links to a new tab.
 * Every renderer that injects paragraph HTML via dangerouslySetInnerHTML goes
 * through this helper.
 */
export function paragraphHtmlToSafeHtml(html: string): string {
  return openExternalLinksInNewTab(expandInlineIcons(sanitizeParagraphHtml(html)));
}
